import * as React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@insolech/ui';
import { emailNotifications, type DeliveryAnnouncementState } from '@insolech/sdk';
import { supabase } from '@/lib/supabase';

/**
 * useAnnouncedDateGuard - warn the admin AT THE MOMENT he re-dates an order a
 * podiatrist has already been promised (Rafael, 2026-08-10).
 *
 * The 12:00 announcement tells a podiatrist an order arrives tomorrow; the
 * 21:00 correction can still fix a date changed that afternoon. A change made
 * after that window reaches nobody: the correction cron for that announcement
 * has run, and the next morning's announcement would land after the promised
 * delivery. No downstream email can fix that, so the information belongs
 * here, where the decision is being made.
 *
 *   never_announced -> apply silently.
 *   window_open     -> apply, and say tonight's correction will carry it.
 *   window_closed   -> STOP and ask, naming BOTH dates and how to reach them.
 *
 * The note is a PORTALLED toast, not inline markup: this hook is used inside
 * a `whitespace-nowrap` table cell, where an inline Alert would render as one
 * unbreakable ~1,100px line and blow the tracker's column widths apart (found
 * in review before it shipped).
 */

export interface AnnouncedDateGuard {
  /** Check first, then either apply or open the warning. `apply` must resolve
   *  when the write has actually landed, so success is never claimed early. */
  guard: (args: { orderId: string; nextDate: string | null; apply: () => Promise<void> }) => void;
  /** Render anywhere: a portalled modal + a portalled toast, no layout cost. */
  element: React.ReactNode;
  checking: boolean;
}

/** Shared by the tracker guard and the prescription dialog notice. */
export function formatPromisedDate(d: string | null | undefined): string {
  if (!d) return 'a date to be confirmed';
  const parsed = new Date(`${d}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return d;
  return parsed.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}

type ClosedState = Extract<DeliveryAnnouncementState, { state: 'window_closed' }>;

export function useAnnouncedDateGuard(): AnnouncedDateGuard {
  const [checking, setChecking] = React.useState(false);
  const [pending, setPending] = React.useState<{
    state: ClosedState;
    nextDate: string | null;
    apply: () => Promise<void>;
  } | null>(null);
  const [note, setNote] = React.useState<{ tone: 'info' | 'warning'; text: string } | null>(null);

  // Toasts clear themselves; working down a delivery list must not leave a
  // wall of undismissed alerts behind.
  React.useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(null), 12000);
    return () => clearTimeout(t);
  }, [note]);

  const guard: AnnouncedDateGuard['guard'] = ({ orderId, nextDate, apply }) => {
    setNote(null);
    setChecking(true);
    void (async () => {
      let state: DeliveryAnnouncementState | null = null;
      try {
        state = await emailNotifications.fetchDeliveryAnnouncementState(supabase, orderId);
      } catch (e) {
        // Never block the edit on a failed lookup - and never hide the miss.
        try {
          await apply();
          setNote({
            tone: 'warning',
            text: `Date saved, but I could not check whether this order was already announced (${
              e instanceof Error ? e.message : String(e)
            }). If it was due tomorrow, tell the podiatrist yourself.`,
          });
        } finally {
          setChecking(false);
        }
        return;
      }

      if (state.state === 'window_closed') {
        setPending({ state, nextDate, apply });
        setChecking(false);
        return;
      }

      try {
        await apply();
        if (state.state === 'window_open') {
          setNote({
            tone: 'info',
            text: `${state.podiatrist_name ?? 'This podiatrist'} was told at 12:00 that this order arrives ${formatPromisedDate(
              state.promised_edd,
            )}. Tonight's 21:00 correction will send them the new date automatically.`,
          });
        }
      } catch {
        // dateMut.onError already surfaces the failure; claiming success here
        // would contradict it.
      } finally {
        setChecking(false);
      }
    })();
  };

  const s = pending?.state;
  const mailto = s?.podiatrist_email
    ? // NOT encodeURIComponent on the address: it would percent-encode the @,
      // which RFC 6068 requires to stay literal.
      `mailto:${s.podiatrist_email}?subject=${encodeURIComponent(
        'Change to your expected delivery',
      )}&body=${encodeURIComponent(
        `Hello ${s.podiatrist_name ?? ''},\n\nWe told you an order was arriving ${formatPromisedDate(
          s.promised_edd,
        )}. That date has changed to ${formatPromisedDate(
          pending?.nextDate,
        )} and I wanted to tell you directly.\n\n`,
      )}`
    : null;

  const element = (
    <>
      {note && typeof document !== 'undefined'
        ? createPortal(
            <div className="fixed bottom-4 end-4 z-50 w-[min(28rem,calc(100vw-2rem))] whitespace-normal">
              <Alert tone={note.tone} title="Delivery notification">
                <div className="flex items-start justify-between gap-2">
                  <span>{note.text}</span>
                  <button
                    type="button"
                    onClick={() => setNote(null)}
                    aria-label="Dismiss"
                    className="shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </Alert>
            </div>,
            document.body,
          )
        : null}

      <Modal open={pending !== null} onOpenChange={(v) => (!v ? setPending(null) : undefined)}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>This podiatrist has already been told</ModalTitle>
          </ModalHeader>
          <ModalBody className="space-y-3">
            <Alert tone="warning" title="No automatic correction will be sent">
              {s?.podiatrist_name ?? 'The podiatrist'} was told on{' '}
              {formatPromisedDate(s?.announced_on)} that this order arrives{' '}
              <strong>{formatPromisedDate(s?.promised_edd)}</strong>
              {s?.corrected_already ? ' (already corrected once)' : ''}. That announcement&apos;s
              correction window has closed, so changing it to{' '}
              <strong>{formatPromisedDate(pending?.nextDate)}</strong> will NOT notify them.
            </Alert>
            <p className="text-text-secondary text-sm">
              Change the date if it is right, then message them yourself.
              {s?.podiatrist_email ? (
                <>
                  {' '}
                  Their address is <span className="font-medium">{s.podiatrist_email}</span>.
                </>
              ) : null}
            </p>
            {mailto ? (
              <a href={mailto} className="text-brand-700 text-sm underline">
                Open an email to them
              </a>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setPending(null)}>
              Leave the date as it is
            </Button>
            <Button
              onClick={() => {
                const p = pending;
                setPending(null);
                if (!p) return;
                setChecking(true);
                void (async () => {
                  try {
                    await p.apply();
                    setNote({
                      tone: 'warning',
                      text: `Date changed to ${formatPromisedDate(p.nextDate)}. ${
                        p.state.podiatrist_name ?? 'The podiatrist'
                      } was NOT notified - message them directly.`,
                    });
                  } catch {
                    // the mutation's own error handler reports the failure
                  } finally {
                    setChecking(false);
                  }
                })();
              }}
            >
              Change the date anyway
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );

  return { guard, element, checking };
}
